import { useDispatch, useSelector } from 'react-redux';
import type { SetRequired } from 'type-fest';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { useOnMount } from '../../../hooks';
import type { PackageInventoryParams } from '../PackageInventoryContainer';
import type { VersionDetails, FetchPackageMetadataResponse } from '../../../../shared/organizationPackages';
import type { AsyncStatus } from '../../../types';

type UsePackageVersionDetails = {
  data: null | VersionDetails & Pick<FetchPackageMetadataResponse, 'name' | 'packageManager'>;
  status: Pick<AsyncStatus, 'errors'> & {
    isLoading: boolean;
  };
};

type PackageInventoryParamsWithVersion = SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>;

/**
 * Hook which handles fetching and getting the packageMetadata/packageVersionMetadata.
 */
const usePackageVersionDetails = ({
  packageLocator,
  version,
}: PackageInventoryParamsWithVersion): UsePackageVersionDetails => {
  const dispatch = useDispatch();

  const packageMetadata = useSelector(PackageInventoryStore.selectors.getPackageMetadata);
  const packageVersionMetadata = useSelector(PackageInventoryStore.selectors.getPackageVersionMetadata);

  const packageMetadataAsyncStatus = useSelector(PackageInventoryStore.selectors.getAsyncStatusGetPackageMetadata);
  const packageVersionMetadataAsyncStatus = useSelector(
    PackageInventoryStore.selectors.getAsyncStatusGetPackageVersionMetadata,
  );

  useOnMount(() => {
    //  If user navigates to this page from the PackageDetails page, then we don't need
    //  to re-fetch the package metadata. Otherwise we still fetch it.
    // TODO: let's make the fetchPackageVersionMetadata endpoint just include the name.
    if (!packageMetadata) {
      dispatch(PackageInventoryStore.actions.fetchPackageMetadata(packageLocator));
    }
    //  Dispatch the action for fetching the package metadata
    dispatch(PackageInventoryStore.actions.fetchPackageVersionMetadata(packageLocator, version));
  }, () => {
    // Set the package version metadata to null on leaving so that we show the loader properly.
    dispatch(PackageInventoryStore.actions.resetPackageVersionDetails());
  });

  return {
    data: !packageMetadata || !packageVersionMetadata ? null : {
      ...packageVersionMetadata,
      // TODO: let's make the fetchPackageVersionMetadata endpoint just include the name + package manager
      name: packageMetadata.name,
      packageManager: packageMetadata.packageManager,
    },
    status: {
      isLoading: packageMetadataAsyncStatus.isLoading || packageVersionMetadataAsyncStatus.isLoading,
      errors: [
        ...(packageMetadataAsyncStatus?.errors ?? []),
        ...(packageVersionMetadataAsyncStatus?.errors ?? []),
      ],
    },
  };
};

export default usePackageVersionDetails;
